import { galaxyOf, nodesOf, scanRange } from "./world";
import { clone } from "./rng";
import type { FactionId, Fleet, GameState, ShipClass } from "./types";
export interface ObservedSystem {
  id: string;
  owner: FactionId | null | "unknown";
  stars: number;
  home: FactionId | null;
  neighbors: string[];
  visible: boolean;
  bastion: number | null;
  grid: number | null;
  mine: number | null;
  research: number | null;
  builtTurn: number | null;
  ships: ShipClass[] | null;
  fleetCount: number | null;
  seen: number;
  barrier: boolean;
}
export interface FactionObservation {
  faction: FactionId;
  turn: number;
  difficulty: GameState["difficulty"];
  credits: number;
  rp: number;
  charge: number;
  shortage: number;
  tech: GameState["factions"][FactionId]["tech"];
  specialUsed: boolean;
  ownTurn: number;
  fleets: Fleet[];
  systems: ObservedSystem[];
  eliminated: FactionId[];
}
export interface FleetContact {
  id: string;
  faction: FactionId;
  system: string;
  flagship: ShipClass;
  size: number;
  own: boolean;
}
/** The UI receives only fleets actually inside the player's live sensor range. */
export function fleetContacts(g: GameState, f: FactionId): FleetContact[] {
  const visible = visibleSystems(g, f),
    strength: Record<ShipClass, number> = {
      scout: 1,
      cruiser: 2,
      dreadnought: 3,
    };
  return g.fleets
    .filter((fl) => fl.faction === f || visible.has(fl.system))
    .map((fl) => ({
      id: fl.id,
      faction: fl.faction,
      system: fl.system,
      flagship: fl.ships.reduce(
        (best, u) => (strength[u.type] > strength[best] ? u.type : best),
        "scout" as ShipClass,
      ),
      size: fl.ships.length,
      own: fl.faction === f,
    }));
}
export function visibleSystems(g: GameState, f: FactionId) {
  const out = new Set<string>();
  for (const s of Object.values(g.systems))
    if (s.owner === f) {
      out.add(s.id);
      if (s.bastion) scanRange(g, s.id, 1).forEach((id) => out.add(id));
    }
  for (const fl of g.fleets.filter((x) => x.faction === f))
    scanRange(g, fl.system, f === "greys" ? 2 : 1).forEach((id) => out.add(id));
  if (g.systems["core-null-observatory"].owner === f)
    scanRange(g, "core-null-observatory", 2).forEach((id) => out.add(id));
  return out;
}
export function updateIntel(g: GameState) {
  for (const f of g.order) {
    const fs = g.factions[f];
    if (fs.eliminated) continue;
    const visible = visibleSystems(g, f),
      known = new Set(fs.discovered);
    for (const s of Object.values(g.systems))
      if (s.owner === f) {
        known.add(s.id);
        nodesOf(g)[s.id].neighbors.forEach((id) => known.add(id));
      }
    visible.forEach((id) => known.add(id));
    fs.discovered = [...known].sort();
    for (const id of visible) {
      const s = g.systems[id],
        fleets = g.fleets.filter((fl) => fl.system === id);
      fs.intel[id] = {
        owner: s.owner,
        seen: g.turn,
        bastion: s.bastion,
        fleetCount: fleets.length,
        ships: [
          ...s.guards.map((u) => u.type),
          ...fleets.flatMap((fl) => fl.ships.map((u) => u.type)),
        ],
      };
    }
  }
}
/** Detached, deliberately incomplete view is the ONLY AI input. No enemy tech or hidden positions. */
export function observe(g: GameState, f: FactionId): FactionObservation {
  const fs = g.factions[f],
    visible = visibleSystems(g, f),
    known = new Set(fs.discovered),
    global =
      g.systems["core-prism-relay"].owner === f ||
      fs.sensorUntil > fs.turnCount;
  if (global) galaxyOf(g).nodes.forEach((n) => known.add(n.id));
  return {
    faction: f,
    turn: g.turn,
    difficulty: g.difficulty,
    credits: fs.credits,
    rp: fs.rp,
    charge: fs.charge,
    shortage: fs.shortage,
    tech: clone(fs.tech),
    specialUsed: fs.specialUsed,
    ownTurn: fs.turnCount,
    fleets: clone(g.fleets.filter((x) => x.faction === f)),
    eliminated: g.order.filter((x) => g.factions[x].eliminated),
    systems: galaxyOf(g)
      .nodes.filter((n) => known.has(n.id))
      .map((n) => {
        const s = g.systems[n.id],
          intel = fs.intel[n.id],
          v = visible.has(n.id),
          own = s.owner === f;
        return {
          id: n.id,
          owner: v || global ? s.owner : intel ? intel.owner : "unknown",
          stars: n.stars,
          home: n.startOwner as FactionId | null,
          neighbors: n.neighbors.filter((id) => known.has(id)),
          visible: v,
          bastion: v ? s.bastion : (intel?.bastion ?? null),
          grid: own ? s.grid : null,
          mine: own ? s.mine : null,
          research: own ? s.research : null,
          builtTurn: own ? s.builtTurn : null,
          ships: v
            ? [
                ...s.guards.map((u) => u.type),
                ...g.fleets
                  .filter((fl) => fl.system === n.id)
                  .flatMap((fl) => fl.ships.map((u) => u.type)),
              ]
            : null,
          fleetCount: v
            ? g.fleets.filter((fl) => fl.system === n.id).length
            : null,
          seen: intel?.seen ?? -1,
          barrier: v && !!s.barrier,
        };
      }),
  };
}
